import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import flvjs from 'flv.js';
import Hls from 'hls.js';
import { getToken } from '../utils/auth';
import { getApiBaseUrl } from '../utils/api';
import { toast } from 'react-hot-toast';
import { useMusic } from '../context/MusicContext';
import SpinnerLoading from '../components/SpinnerLoading';
import { FaPlay, FaVideo, FaFilm, FaArrowLeft, FaComment, FaCog } from 'react-icons/fa';
import LiveChatSidebar from '../components/LiveChatSidebar';
import StreamManagement from '../components/Admin/StreamManagement'; // Make sure this is exported/accessible
import { useStreamViewers } from '../hooks/useStreamViewers';
import { MdPlayArrow } from 'react-icons/md'; // Use react-icons instead of MUI to avoid resolve errors

const LiveStreamPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    // Manual User Decoding (since AuthContext is missing)
    const [currentUser, setCurrentUser] = useState<any>(null);
    useEffect(() => {
        const token = getToken();
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                setCurrentUser(payload);
            } catch (e) {
                console.error("Invalid token", e);
            }
        }
    }, []);

    const [loading, setLoading] = useState(true);
    const [stream, setStream] = useState<any>(null); // For single stream view
    const [streamList, setStreamList] = useState<any[]>([]); // For list view
    const [showChat, setShowChat] = useState(true); // Chat sidebar visibility
    const [showManageModal, setShowManageModal] = useState(false); // Admin Manage Modal
    const liveViewers = useStreamViewers(stream?.id);

    // Restore Refs and Context that were lost
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<any>(null);
    const { setIsPlaying } = useMusic();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    // Resize Effect
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Effect to hide chat initially if disabled (will update when stream loads)
    useEffect(() => {
        if (stream && stream.chatEnabled === false) {
            setShowChat(false);
        }
    }, [stream]);

    // Main Load Effect
    useEffect(() => {
        // Pause background music when viewing live stream (only if watching)
        if (id) {
            setIsPlaying(false);
        }

        if (id) {
            loadStream(id);
        } else {
            loadStreamList();
        }

        return () => {
            // Cleanup player on unmount
            if (playerRef.current) {
                try {
                    // Safety check before destroy
                    if (typeof playerRef.current.destroy === 'function') {
                        playerRef.current.destroy();
                    }
                } catch (err) {
                    console.warn('Player cleanup error:', err);
                }
                playerRef.current = null;
            }
        };
    }, [id]);

    const loadStream = async (streamId: string) => {
        try {
            setLoading(true);
            const token = getToken();
            // Allow public access (no token check here)

            const API_URL = getApiBaseUrl();
            const headers: any = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/stream/current?id=${streamId}`, {
                headers: headers
            });

            const data = await response.json();

            if (data.stream) {
                setStream(data.stream);
                // Initialize player after stream is loaded
                setTimeout(() => initPlayer(data.stream.url), 100);
            } else {
                toast.error('Không tìm thấy stream');
                navigate('/live'); // Redirect to list if not found
            }
        } catch (error) {
            console.error('Load stream error:', error);
            toast.error('Không thể tải stream');
        } finally {
            setLoading(false);
        }
    };

    const loadStreamList = async () => {
        try {
            setLoading(true);
            const token = getToken();
            // Allow public access

            const API_URL = getApiBaseUrl();
            const headers: any = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/stream/list`, {
                headers: headers
            });
            const data = await response.json();
            if (data.streams) {
                setStreamList(data.streams);
            }
        } catch (error) {
            console.error('Load list error:', error);
        } finally {
            setLoading(false);
        }
    };

    const initPlayer = (streamUrl: string) => {
        if (!videoRef.current) return;

        // Destroy existing player
        if (playerRef.current) {
            playerRef.current.destroy();
            playerRef.current = null;
        }

        const isHls = streamUrl.includes('.m3u8') || streamUrl.includes('placeholder'); // Basic check

        if (isHls) {
            if (Hls.isSupported()) {
                const hls = new Hls();
                hls.loadSource(streamUrl);
                hls.attachMedia(videoRef.current);
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    attemptAutoplay();
                });
                hls.on(Hls.Events.ERROR, (event, data) => {
                    console.error('HLS error:', data);
                    if (data.fatal) {
                        // toast.error('Lỗi phát stream HLS');
                    }
                });
                playerRef.current = hls;
            } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
                // Native HLS support (Safari)
                videoRef.current.src = streamUrl;
                videoRef.current.addEventListener('loadedmetadata', () => {
                    attemptAutoplay();
                });
            } else {
                toast.error('Trình duyệt không hỗ trợ HLS');
            }
        } else {
            // FLV fallback
            if (flvjs.isSupported()) {
                const player = flvjs.createPlayer({
                    type: 'flv',
                    url: streamUrl,
                    isLive: true,
                    hasAudio: true,
                    hasVideo: true
                }, {
                    enableWorker: false,
                    enableStashBuffer: false,
                    stashInitialSize: 128,
                    // Disable verbose logging
                    lazyLoad: false,
                    lazyLoadMaxDuration: 3 * 60,
                    lazyLoadRecoverDuration: 30
                });

                // Disable all FLV.js logs
                flvjs.LoggingControl.forceGlobalTag = true;
                flvjs.LoggingControl.globalTag = 'flv.js';
                flvjs.LoggingControl.enableAll = false;
                flvjs.LoggingControl.enableDebug = false;
                flvjs.LoggingControl.enableVerbose = false;
                flvjs.LoggingControl.enableInfo = false;
                flvjs.LoggingControl.enableWarn = false;
                flvjs.LoggingControl.enableError = false;

                player.attachMediaElement(videoRef.current);
                player.load();
                playerRef.current = player;

                attemptAutoplay();

                player.on(flvjs.Events.ERROR, (errorType: string, errorDetail: string) => {
                    console.error('FLV Player error:', errorType, errorDetail);
                    // toast.error('Lỗi phát stream FLV');
                });
            } else {
                toast.error('Trình duyệt không hỗ trợ FLV');
            }
        }
    };

    const attemptAutoplay = () => {
        if (!videoRef.current) return;
        // Ensure muted for autoplay
        videoRef.current.muted = true;

        var playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
            playPromise.then((_) => {
                // Automatic playback started!
            }).catch((error) => {
                // Auto-play was prevented
                console.log('Autoplay prevented:', error);
                toast((t) => (
                    <div className="flex items-center gap-2">
                        <MdPlayArrow className="text-white text-xl" />
                        <span className="font-medium">Vui lòng bấm Play để xem</span>
                    </div>
                ), {
                    style: {
                        background: '#ef4444',
                        color: '#fff',
                        fontWeight: '500',
                        padding: '12px 16px',
                        borderRadius: '8px',
                    },
                    icon: null,
                    duration: 5000
                });
            });
        }
    };
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div style={{ transform: 'scale(0.435)' }}>
                    <SpinnerLoading />
                </div>
            </div>
        );
    }

    // LIST VIEW (No ID)
    if (!id) {
        if (streamList.length === 0) {
            return (
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                            <FaVideo className="w-10 h-10 text-gray-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Chưa có nội dung
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400">
                            Hiện tại không có stream hoặc phim nào đang phát.
                        </p>
                        {currentUser?.role === 'admin' && (
                            <button
                                onClick={() => setShowManageModal(true)}
                                className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-all shadow-lg flex items-center gap-2 mx-auto"
                            >
                                <FaCog />
                                <span>Thêm Nội Dung Ngay</span>
                            </button>
                        )}
                    </div>
                    {/* Manage Stream Modal (In Empty State) */}
                    {showManageModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fadeIn">
                            <div className="bg-white dark:bg-gray-900 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-scaleIn">
                                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Quản lý Live & Phim</h3>
                                    <button
                                        onClick={() => setShowManageModal(false)}
                                        className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    >
                                        ✕
                                    </button>
                                </div>
                                <div className="p-6 max-h-[80vh] overflow-y-auto">
                                    <StreamManagement />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 animate-fadeIn">
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
                    {/* Hero Section */}
                    <div className="mb-8 rounded-2xl bg-gradient-to-br from-red-600 via-pink-700 to-purple-900 p-6 sm:p-10 shadow-2xl relative overflow-hidden animate-slideDownIn">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
                        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-400/10 rounded-full blur-3xl mix-blend-overlay"></div>
                        <div className="relative z-10 text-white w-full">
                            <div className="relative">
                                <div className="pr-12 sm:pr-0 sm:flex sm:justify-between sm:items-start sm:gap-4">
                                    <div>
                                        <h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-3 flex items-center gap-2 sm:gap-3">
                                            <FaVideo className="animate-pulse" />
                                            <span>Live & Movies</span>
                                        </h1>
                                        <p className="text-red-100 text-sm sm:text-lg opacity-90 max-w-2xl font-mono leading-relaxed">
                                            Thưởng thức các bộ phim bom tấn và sự kiện trực tiếp cùng cộng đồng.
                                        </p>
                                    </div>
                                    {currentUser?.role === 'admin' && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowManageModal(true);
                                            }}
                                            className="absolute top-0 right-0 sm:static bg-white/20 hover:bg-white/30 text-white w-9 h-9 sm:w-auto sm:h-auto sm:px-4 sm:py-2 rounded-lg backdrop-blur-sm transition-all flex items-center justify-center gap-2 border border-white/10 shadow-lg"
                                        >
                                            <FaCog className="text-lg" />
                                            <span className="hidden sm:inline font-bold">Quản lý</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-4">
                        {streamList.map((item, index) => (
                            <div
                                key={item.id}
                                onClick={() => navigate(`/live/${item.id}`)}
                                className="group flex flex-row bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer border border-gray-100 dark:border-gray-700 animate-slideUpIn h-28"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                {/* Thumbnail (Portrait/Poster Style) */}
                                <div className="w-20 h-full bg-gray-200 dark:bg-gray-700 relative overflow-hidden flex-shrink-0">
                                    {item.thumbnail ? (() => {
                                        // Construction Logic: Use Optimized File Proxy
                                        // item.thumbnail is "/uploads/thumbnails/filename.ext"
                                        const filename = item.thumbnail.split('/').pop();
                                        const imageUrl = item.thumbnail.startsWith('http')
                                            ? item.thumbnail
                                            : `${getApiBaseUrl()}/stream/image?file=${filename}`;

                                        return (
                                            <img
                                                src={imageUrl}
                                                alt={item.title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                onError={(e) => {
                                                    // console.error('Image Load Failed:', imageUrl);
                                                    e.currentTarget.style.display = 'none';
                                                    e.currentTarget.parentElement?.classList.add('fallback-icon-visible');
                                                }}
                                            />
                                        );
                                    })() : (
                                        <div className="absolute inset-0 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                                            {item.type === 'live' ? (
                                                <FaVideo className="text-3xl text-gray-400 dark:text-gray-600 opacity-50" />
                                            ) : (
                                                <FaFilm className="text-3xl text-gray-400 dark:text-gray-600 opacity-50" />
                                            )}
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />

                                    {/* Play Overlay (Mini) */}
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <div className="w-8 h-8 rounded-full bg-red-600/90 text-white flex items-center justify-center shadow-lg transform scale-0 group-hover:scale-100 transition-transform duration-300">
                                            <FaPlay className="ml-0.5 text-xs" />
                                        </div>
                                    </div>

                                    {/* Live Badge Only (Smaller) */}
                                    {item.type === 'live' && (
                                        <div className="absolute top-1 left-1">
                                            <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm animate-pulse">
                                                TRỰC TIẾP
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Info Section */}
                                <div className="p-3 flex flex-col justify-center flex-1 min-w-0">
                                    <div className="flex justify-between items-start">
                                        <h3 className="font-bold text-gray-900 dark:text-white text-sm sm:text-base mb-1 line-clamp-1 group-hover:text-red-500 transition-colors">
                                            {item.title}
                                        </h3>
                                        {/* Movie/Type Badge in Text */}
                                        <span className={`flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-${item.color || 'red'}-100 text-${item.color || 'red'}-600 dark:bg-${item.color || 'red'}-900/30`}>
                                            {item.type === 'live' ? 'TRỰC TIẾP' : 'PHIM'}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                                        <span className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700/50 px-1.5 py-0.5 rounded">
                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                            {new Date(item.createdAt).toLocaleDateString('vi-VN')}
                                        </span>
                                    </div>

                                    <p className="text-gray-500 dark:text-gray-400 text-xs line-clamp-1 mt-1">
                                        {item.type === 'live'
                                            ? 'Phát sóng trực tiếp chất lượng cao'
                                            : 'Phim HD • Vietsub • Cập nhật mới nhất'}
                                    </p>


                                </div>
                                {/* Right Arrow / Play Button (Desktop) */}
                                <div className="hidden sm:flex items-center justify-center px-6 border-l border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 w-24 flex-shrink-0 group-hover:bg-red-50 dark:group-hover:bg-red-900/10 transition-colors">
                                    <div className="w-10 h-10 rounded-full bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-500 flex items-center justify-center group-hover:text-red-500 group-hover:shadow transition-all duration-300">
                                        <FaPlay className="ml-1 w-4 h-4" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Manage Stream Modal */}
                    {showManageModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fadeIn">
                            <div className="bg-white dark:bg-gray-900 w-full h-full shadow-2xl overflow-hidden animate-scaleIn flex flex-col">
                                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-800">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                        <FaCog className="text-gray-500" />
                                        <span>Quản lý Live Stream & Phim</span>
                                    </h3>
                                    <button
                                        onClick={() => setShowManageModal(false)}
                                        className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-xl text-gray-500 dark:text-gray-400"
                                    >
                                        ✕
                                    </button>
                                </div>
                                <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900">
                                    <StreamManagement />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }


    // WATCH VIEW (Has ID)
    if (!stream) {
        // Should have handled in loadStream, but fallback
        return null;
    }

    return (
        <div className="flex flex-col lg:flex-row h-[100dvh] bg-black overflow-hidden font-sans">
            {/* Main Content: Video & Header */}
            <div className={`${showChat ? 'flex-none lg:flex-1' : 'flex-1'} flex flex-col relative min-w-0 bg-black`}>
                {/* Header Bar - Desktop & Mobile (Overlay on Mobile?) or just keep as is */}
                <div className="h-14 min-h-[56px] bg-[#1f1f23] flex items-center justify-between px-4 border-b border-gray-800 z-20 flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            onClick={() => navigate('/live')}
                            className="p-2 -ml-2 text-gray-400 hover:text-white rounded-full hover:bg-white/10 transition-all"
                            title="Quay lại danh sách"
                        >
                            <FaArrowLeft />
                        </button>

                        <div className="flex flex-col min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="bg-red-600/90 text-white text-[10px] uppercase font-bold px-1.5 rounded animate-pulse whitespace-nowrap">
                                    TRỰC TIẾP
                                </span>
                                <h1 className="text-gray-100 font-bold text-sm sm:text-base truncate">
                                    {stream.title}
                                </h1>
                            </div>
                            <p className="text-gray-400 text-xs truncate hidden sm:block">
                                Đang phát sóng • {liveViewers} người đang xem
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="hidden lg:block">
                            {!showChat && (
                                <button
                                    onClick={() => setShowChat(true)}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-[#2a2a30] hover:bg-[#3f3f46] text-white rounded-full text-xs font-bold transition-all border border-gray-700"
                                >
                                    <FaComment />
                                    <span>Hiện chat</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Video Player Container */}
                <div className={`relative bg-black flex items-center justify-center overflow-hidden ${showChat ? 'flex-shrink-0 lg:flex-1 w-full aspect-video lg:aspect-auto lg:h-auto border-b lg:border-none border-gray-800' : 'flex-1'}`}>
                    <video
                        ref={videoRef}
                        className="w-full h-full object-contain"
                        controls
                        autoPlay
                        muted
                        playsInline
                        onLoadedMetadata={(e) => {
                            // ... (Metadata logic preserved)
                            if (stream && stream.type !== 'live') {
                                const savedTime = localStorage.getItem(`stream_progress_${stream.id}`);
                                if (savedTime) e.currentTarget.currentTime = parseFloat(savedTime);
                            }
                        }}
                        onTimeUpdate={(e) => {
                            // ... (Time logic preserved)
                            if (stream && stream.type !== 'live') {
                                localStorage.setItem(`stream_progress_${stream.id}`, e.currentTarget.currentTime.toString());
                            }
                        }}
                        onClick={handlePlay}
                    />
                </div>
            </div>

            {/* Sidebar / Bottom Section: Controls + Chat */}
            {showChat && (
                <div className="w-full lg:w-96 flex flex-col bg-[#1f1f23] border-l border-gray-800 lg:h-full flex-1 min-h-0 lg:flex-none animate-slideLeftIn">
                    {/* Chat Component (Fills remaining space) */}
                    <div className="flex-1 min-h-0 relative">
                        <LiveChatSidebar
                            streamId={id || ''}
                            onClose={() => setShowChat(false)}
                            isMobileEmbedded={isMobile}
                            chatEnabled={stream?.chatEnabled ?? true}
                        />
                    </div>
                </div>
            )}

            {/* Mobile Re-open Chat Button (Floating) */}
            {!showChat && (
                <button
                    onClick={() => setShowChat(true)}
                    className="lg:hidden fixed bottom-4 right-4 z-50 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-blue-700 transition-all animate-bounce"
                >
                    <FaComment />
                </button>
            )}
        </div>
    );

    function handlePlay() {
        if (videoRef.current) {
            videoRef.current.play().catch(e => console.error("Play failed", e));
        }
    }
};

export default LiveStreamPage;
